import { HttpMethodEnum } from "main/common/enums/HttpMethodEnum";
import { RequestHelper } from "main/common/request-manager/RequestHelper";
import { AppStateUtils } from "main/common/utils/AppStateUtils";
import { SystemConfig } from "main/config/SystemConfig";
import React, { useEffect, useState } from "react"
import * as One from "common/utils/One"
import { RowCP } from "main/common/components/grid/RowCP";
import { ColumnCP } from "main/common/components/grid/ColumnCP";
import { IDateRangeFilter } from "main/common/components/date-range-picker/inner/IDateRangeFilter";
import moment from "moment";
import { DateRangePickerCP } from "main/common/components/date-range-picker/DateRangePickerCP";
import styled from "styled-components";
import { TableCP } from "main/common/components/table/TableCP";
import { CardCP } from "main/common/components/card/CardCP";
import { SelectCP } from "main/common/components/form-fields/select/SelectCP";
import { ButtonCP } from "main/common/components/button/ButtonCP";
import { notification } from 'antd';
import { ExcelUtil } from "main/modules/people/utils/ExcelUtil";
import { MaskUtils } from "main/common/utils/MaskUtils";
import { InputMaskTypeEnum } from "main/common/enums/InputMaskTypeEnum";

notification.config({
    placement: 'bottomRight',
});

const colunas = [
    {
        title: 'ID Lead',
        dataIndex: 'idlead',
        key: 'idlead',
    },
    {
        title: 'Nome',
        dataIndex: 'nome',
        key: 'nome',
        render: (txt: any, dados: any) => (dados.nome + '').toLocaleUpperCase()
    },
    {
        title: 'Telefone',
        dataIndex: 'telefonelead',
        key: 'telefonelead',
        render: (txt: any, dados: any) => MaskUtils.applyMask(dados.telefonelead, InputMaskTypeEnum.PHONE)
    },
    {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
    },
];

export function RelatorioLeadsPorIndicacao(): JSX.Element {
    const [dateFilters, setDateFilters] = useState<IDateRangeFilter>({
        beginDate: moment().startOf('month').toDate(),
        endDate: moment().endOf('month').toDate()
    });
    const [indicacoes, setIndicacoes] = useState<any[]>([]);
    const [indicacaoSelecionada, setIndicacaoSelecionada] = useState<string | undefined>();
    const [dados, setDados] = useState<any[]>([]);
    const [carregando, setCarregando] = useState(false);
    const franquia = AppStateUtils.getCurrentFranchise();

    useEffect(() => { carregarIndicacoes(); }, []);
    useEffect(() => {
        if (indicacaoSelecionada) carregarDados();
        else setDados([]);
    }, [dateFilters, indicacaoSelecionada]);

    function mudouDatas(n: any) {
        setDateFilters(n);
    }

    function mudouIndicacao(valor: any) {
        setIndicacaoSelecionada(valor);
    }

    async function carregarIndicacoes() {
        const resultado: any = await RequestHelper.runRequest({
            url: `${SystemConfig.getInstance().apiBaseUrl}/dashboard/obter-indicacoes`,
            method: HttpMethodEnum.POST,
            params: {
                schema: AppStateUtils.getDomainSlug()
            }
        });

        if (One.ok(resultado)) {
            const lista = One.dados(resultado) ?? [];
            const opcoes = lista.map((i: any) => ({
                value: i.ops_name,
                label: i.ops_name,
                key: i.ops_name
            }));
            setIndicacoes(opcoes);
        }
    }

    async function carregarDados() {
        setCarregando(true);
        const resultado: any = await RequestHelper.runRequest({
            url: `${SystemConfig.getInstance().apiBaseUrl}/dashboard/leads-por-indicacao`,
            method: HttpMethodEnum.POST,
            params: {
                de: One.dateParaSQL(dateFilters?.beginDate),
                ate: One.dateParaSQL(dateFilters?.endDate),
                codigoFranquia: AppStateUtils.getCurrentFranchise()?.code,
                indicacao: indicacaoSelecionada,
                schema: AppStateUtils.getDomainSlug()
            }
        }).finally(() => setCarregando(false));

        if (One.ok(resultado)) {
            setDados(One.dados(resultado) ?? []);
            return;
        }
        setDados([]);
    }

    function exportarCSV() {
        if (!dados || !dados.length) {
            notification.open({
                message: 'Sem Dados',
                description: (
                    <div>
                        <b>Não há dados para serem exportados.</b>
                    </div>
                ),
                duration: 3,
            });
            return;
        }

        let r = `RELATORIO DE LEADS POR INDICACAO\nIndicação;${indicacaoSelecionada}\n`;
        r += ExcelUtil.excel(dados, [
            ['ID LEAD', 'idlead'],
            ['NOME', 'nome'],
            ['TELEFONE', 'telefonelead'],
            ['STATUS', 'status']
        ]);
        r += `\n`;

        ExcelUtil.salvarTexto('leads-por-indicacao.CSV', r);
    }

    return (
        <AreaFormatada>
            <CardCP innerSpacing={'none'} overflow={'auto'}>
                <div>
                    <div className="titulo-franquia">
                        <div>Unidade</div>
                        {franquia?.code} - {franquia?.name}
                    </div>
                    {carregando ? 'Carregando... ' : ''}
                </div>
            </CardCP>

            <RowCP spaceBetweenCols={20} flex={true} justify="end">
                <ButtonCP
                    icon={'table'}
                    size={'small'}
                    type={'primary'}
                    tooltip={'Clique para exportar em excel'}
                    onClick={() => exportarCSV()}
                >
                    Exportar
                </ButtonCP>
            </RowCP>

            <RowCP spaceBetweenCols={20} align={'middle'}>
                <ColumnCP size={8}>
                    <DateRangePickerCP
                        label={'Intervalo de Tempo'}
                        value={dateFilters}
                        onChange={mudouDatas}
                        fastFilter={'all'}
                        allowClear={false}
                        showBorder={true}
                    />
                </ColumnCP>
                <ColumnCP size={6}>
                    <div className="seletor-indicacao">
                        <SelectCP<any>
                            label={'Indicação'}
                            options={indicacoes}
                            value={indicacaoSelecionada}
                            onChange={mudouIndicacao}
                            allowClear={true}
                            fieldName={'label'}
                        />
                    </div>
                </ColumnCP>
            </RowCP>

            <CardCP innerSpacing={'normal'}>
                {!indicacaoSelecionada ? (
                    <div className="aviso">Selecione uma indicação para gerar o relatório.</div>
                ) : (
                    <TableCP
                        columns={colunas}
                        data={dados ?? []}
                        loading={carregando}
                    />
                )}
            </CardCP>
        </AreaFormatada>
    );
}

const AreaFormatada = styled.div`
    .titulo-franquia{
        text-align:center;
        font-weight: 645;
        font-size: 20px;
        letter-spacing: -0.048em;
    }

    .seletor-indicacao{
        position: relative;
        top: 14px;
    }

    .aviso{
        text-align:center;
        padding: 24px;
        font-weight: 500;
        color: #666;
    }
`;
